import React from "react"
import { Link } from "react-router-dom"
import cx from "classnames"

export const Button = ({ className = "", ...props }) => (
  <button
    type="button"
    className={cx(
      `${
        props.bg ? props.bg : "bg-green-700"
      } text-white font-bold rounded focus:outline-none text-base py-2 px-4`,
      props.disabled && "opacity-50",
      className,
    )}
    {...props}
  />
)

// background: #00ff00;
// color: #ff0000;

export const SecondaryButton = ({
  children,
  className = "",
  active = false,
  ...props
}) =>
  props.to ? (
    <Link
      className={cx(
        `flex bg-black-800 text-bn-neon-red font-medium rounded focus:outline-none text-base text-center py-2 px-4 border-2 border-black-800`,
        props.disabled && "opacity-50",
        active
          ? "bg-black font-bold border-bn-neon-red border-2"
          : "hover:bg-black hover:font-bold hover:uppercase hover:border-bn-neon-red",
        className,
      )}
      to={props.to}
    >
      {children}
    </Link>
  ) : (
    <button
      type="button"
      className={cx(
        `flex bg-gray-300 font-bold rounded focus:outline-none text-base py-2 px-4`,
        props.disabled && "opacity-50",
        active
          ? "bg-gray-900 text-white"
          : "hover:bg-gray-400 hover:text-black-800",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )

export const InterestedButton = ({
  className = "",
  renderMode = "full",
  ...props
}) =>
  renderMode === "minimal" ? (
    <button
      type="button"
      className={cx(
        `${
          props.bg ? props.bg : "bg-green-700"
        } focus:outline-none font-bold px-2 py-1 rounded text-sm text-white min-w-32 whitespace-nowrap`,
        props.disabled && "opacity-50",
        className,
      )}
      {...props}
    />
  ) : renderMode === "full" ? (
    <button
      type="button"
      className={cx(
        `${
          props.bg ? props.bg : "bg-green-700"
        } text-white font-bold rounded focus:outline-none text-base py-2 px-4 min-w-36 whitespace-nowrap`,
        props.disabled && "opacity-50",
        className,
      )}
      {...props}
    />
  ) : renderMode === "text" ? (
    <button className="text-link" />
  ) : null

export const SmallButton = ({ className = "", ...props }) => (
  <button
    type="button"
    className={cx(
      `${
        props.bg ? props.bg : "bg-green-700"
      } text-white font-bold rounded focus:outline-none text-sm py-1 px-2`,
      props.disabled && "opacity-50",
      className,
    )}
    {...props}
  />
)

export const XSmallButton = ({ className = "", ...props }) => (
  <button
    type="button"
    className={cx(
      `${
        props.bg ? props.bg : "bg-white"
      } text-gray-700 border border-gray-700 font-bold rounded focus:outline-none text-xs py-1 px-2`,
      props.disabled && "opacity-50",
      className,
    )}
    {...props}
  />
)

export const ShowHideButton = ({ className = "", ...props }) => (
  <div className="pb-2 pt-1">
    <SmallButton
      onClick={(e) =>
        props
          .updateStatus()
          .then(() =>
            props.setPostStatus(
              props.postStatus === "published" ? "trashed" : "published",
            ),
          )
      }
      bg={props.postStatus === "published" ? "bg-red-400" : "bg-green-700"}
    >
      {props.postStatus === "published" ? "Hide Post" : "Show Post"}
    </SmallButton>
  </div>
)

export const LinkButton = ({ className = "", to, ...props }) => (
  <Link
    to={to}
    className={cx(
      `inline-block text-center ${
        props.bg ? props.bg : "bg-green-700"
      } text-white font-bold rounded focus:outline-none text-base py-2 px-4`,
      props.disabled && "opacity-50",
      className,
    )}
    {...props}
  />
)

export const AnchorButton = ({ href, children, target }) => (
  <a
    className="text-white font-bold rounded focus:outline-none py-1 px-2 bg-green-700 inline-block text-center"
    href={href}
    target={target}
  >
    {children}
  </a>
)

export const SubmitButton = ({
  className,
  active,
  bg = "bg-green-700",
  ...props
}) => (
  <button
    type="submit"
    disabled={!active}
    className={cx(
      "text-white font-bold rounded focus:outline-none uppercase text-xl py-2 px-2 w-full",
      !active && "opacity-50",
      bg,
      className,
    )}
    {...props}
  />
)

export const HomePageButton = ({
  className = "",
  bgColor = "bg-black-800",
  textColor = "text-white",
  active = true,
  ...props
}) => (
  <button
    type="submit"
    disabled={!active}
    className={cx(
      "hover:opacity-25 py-3 px-12 rounded font-medium focus:outline-none",
      !active && "opacity-50",
      bgColor,
      textColor,
      className,
    )}
    {...props}
  />
)

export const BroadcastButton = ({ className, active, ...props }) => (
  <button
    type="button"
    className={cx(
      "text-white font-bold rounded focus:outline-none text-base py-2 w-full",
      active ? "bg-red-400" : "bg-green-700",
      className,
    )}
    {...props}
  />
)

export const GuestManagerButton = ({ disabled = false, ...props }) => (
  <button
    disabled={disabled}
    className={cx(
      "focus:outline-none border border-gray-500 text-gray-700 px-2 rounded-sm",
      disabled ? "opacity-50" : "",
    )}
    {...props}
  />
)

export const OutlineButton = ({
  disabled = false,
  className = "",
  ...props
}) => (
  <button
    disabled={disabled}
    className={cx(
      // No text color set here on purpose - every current caller passes its
      // own (text-gray-700/800 via className), and this used to hardcode
      // text-white, which is the wrong default for a button with no
      // background fill anyway. Same-specificity classes don't resolve by
      // className string order, only by which rule lands later in the
      // compiled stylesheet - see the FollowButton w-auto/w-full fix for
      // the same underlying issue.
      "focus:outline-none border text-base border-gray-500 px-2 py-1 rounded-sm align-bottom",
      disabled ? "opacity-50" : "",
      className,
    )}
    {...props}
  />
)

export const FollowButton = ({ className, active, variant = "filled", ...props }) => (
  <button
    type="button"
    className={cx(
      // No width utility here on purpose - a button's natural width is
      // already "auto", and callers pass their own (e.g. w-full in
      // BandListItem) via className. Applying w-auto here too meant two
      // same-specificity width classes on one element, whichever's CSS rule
      // happened to land later in the compiled stylesheet won - a relic of
      // Tailwind's old AOT compiler's fixed generation order, which broke
      // once JIT's discovery-order-based generation stopped guaranteeing
      // w-full would sort after w-auto.
      "font-bold focus:outline-none px-4 whitespace-nowrap",
      variant === "outline"
        ? cx(
            "bg-transparent border rounded-sm text-sm py-1",
            active
              ? "border-red-400 text-red-400"
              : "border-green-700 text-green-700",
          )
        : cx(
            "text-white text-xs md:text-base py-2 rounded-b",
            active ? "bg-red-400" : "bg-green-700",
          ),
      className,
    )}
    {...props}
  />
)
export const SignInButton = ({ className, active, ...props }) => (
  <button
    type="button"
    className={cx(
      "text-white font-bold rounded focus:outline-none hover:bg-blue-700 focus:shadow-outline text-base py-2 px-4",
      active ? "bg-blue-500" : "bg-blue-200",
      className,
    )}
    {...props}
  />
)
